import { useRef, useState, useEffect, useCallback } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { ChevronDown, ChevronUp, X, Check } from 'lucide-react';

export type Option = {
  value: string;
  label: string;
};

type Props = {
  options: Array<Option> | undefined;
  value: Array<Option>;
  title: string;
  isLoading: boolean;
  isMulti?: boolean;
  showSelections?: boolean;
  onChange?: (values: Array<Option>) => void;
  dropdownWidth?: number;
};

const CheckboxSelect = ({
  value,
  options,
  title,
  onChange,
  showSelections = false,
  dropdownWidth,
}: Props) => {
  const [open, setOpen] = useState<boolean>(false);
  const [focused, setFocused] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

 const processPastedText = useCallback(
    (pastedText: string) => {
      if (!pastedText || !options || !onChange) return;

      const normalize = (str: string) => str.trim().toLowerCase();

      const items = pastedText
        .split(/[\n\r\t]+/)
        .map(item => item.trim())
        .filter(Boolean);

      if (!items.length) return;

      const normalizedItems = items.map(normalize);

      const matchedOptions = options.filter(option => {
        const valueNorm = normalize(option.value);
        const labelNorm = normalize(option.label);
        return normalizedItems.includes(valueNorm) || normalizedItems.includes(labelNorm);
      });

      if (matchedOptions.length > 0) {
        const newValues = [
          ...value,
          ...matchedOptions.filter(option => !value.some(v => v.value === option.value)),
        ];

        onChange(newValues);
        setInputValue('');
        setFocused(false);

        requestAnimationFrame(() => {
          contentRef.current?.scrollTo({ top: 0 });
        });
      }
    },
    [options, value, onChange]
  );

  useEffect(() => {
    if (focused && inputRef.current) inputRef.current.focus();
  }, [focused]);

  useEffect(() => {
    if (!open) return;

    const handlePaste = (event: ClipboardEvent) => {
      event.preventDefault();
      const text = event.clipboardData?.getData('text');
      if (text) processPastedText(text);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const mod = event.ctrlKey || event.metaKey;
      const key = event.key.toLowerCase();

      if (mod && (key === 'c' || event.keyCode === 67) && value.length) {
        event.preventDefault();
        const text = value.map(o => o.label).join('\n');
        navigator.clipboard?.writeText(text).catch(() => {});
      }
    };

    window.addEventListener('paste', handlePaste, true);
    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('paste', handlePaste, true);
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [open, processPastedText, value]);

  const handleToggleOpen = () => {
    const newOpen = !open;
    setOpen(newOpen);
    setFocused(newOpen);
    if (!newOpen) setInputValue('');
  };

  const handleDoubleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!focused) {
      setFocused(true);
      if (!open) {
        setOpen(true);
      }
    }
  };

  const handleSelect = (optionValue: string) => {
    if (!onChange || !options) return;

    const isSelected = value.some(selectedOption => selectedOption.value === optionValue);

    onChange(
      isSelected
        ? value.filter(selectedOption => selectedOption.value !== optionValue)
        : [...value, options.find(option => option.value === optionValue)!]
    );
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange?.([]);
  };

  const handleInputChange = (input: string) => {
    setInputValue(input);
    if (!options || !onChange) return;

    const items = input
      .split(/[,\s]+/)
      .map(item => item.trim())
      .filter(Boolean);

    if (items.length < 2) return;

    const normalize = (s: string) => s.trim().toLowerCase();
    const normalizedItems = items.map(normalize);
    const selectedOptions = options.filter(option =>
      normalizedItems.includes(normalize(option.value))
    );

    if (selectedOptions.length) {
      const newValues = [
        ...value,
        ...selectedOptions.filter(option => !value.some(v => v.value === option.value)),
      ];
      onChange(newValues);
      setInputValue('');
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setFocused(false);
    }, 200);
  };

  const getDisplayValue = () => {
    if (value.length === 0) return '';
    if (showSelections) return value.map(option => option.label).join(', ');
    if (value.length === 1) return value[0].label;

    return `${value.length} элемент(а)`;
  };

  const handleIconClick = (event: React.MouseEvent) => {
    if (value.length > 0) {
      event.stopPropagation();
      handleClear(event);
    }
  };

  const handleOptionClick = (event: React.MouseEvent, optionValue: string) => {
    event.stopPropagation();
    handleSelect(optionValue);
  };

  const selectedValues = value.map(value => value.value);
  const selectedSet = new Set(selectedValues);
  const optionsListRaw = options || [];
  const optionsList = optionsListRaw
    .map((option, index) => ({ option, index }))
    .sort((a, b) => {
      const aSelected = selectedSet.has(a.option.value);
      const bSelected = selectedSet.has(b.option.value);
      if (aSelected !== bSelected) return aSelected ? -1 : 1;
      return a.index - b.index;
    })
    .map(x => x.option);

  const normalizedQuery = inputValue.trim().toLowerCase();
  const filteredOptionsList = normalizedQuery
    ? optionsList.filter(option => {
        const value = option.value.toLowerCase();
        const label = option.label.toLowerCase();
        return value.includes(normalizedQuery) || label.includes(normalizedQuery);
      })
    : optionsList;

  return (
    <Popover.Root
      open={open}
      onOpenChange={(isOpen: boolean) => {
        setOpen(isOpen);
        if (!isOpen) {
          setFocused(false);
          setInputValue('');
        }
      }}
    >
      <div
        className="rounded-lg p-[10px] bg-gray-800 cursor-pointer relative text-[12px] h-[34px] inline-flex items-center gap-[20px]"
        onClick={handleToggleOpen}
      >
        <Popover.Trigger asChild>
          <div className="flex items-center gap-[20px] min-w-0 w-full">
            {!value.length ? (
              <span
                className="text-white text-[12px] whitespace-nowrap"
                onDoubleClick={handleDoubleClick}
                title="Откройте список, затем ищите или вставляйте (Ctrl+V)"
              >
                {title}
              </span>
            ) : (
              <span
                className="text-white text-[12px] overflow-hidden text-ellipsis whitespace-nowrap"
                onDoubleClick={handleDoubleClick}
                title="Откройте список, затем ищите или вставляйте (Ctrl+V)"
              >
                {getDisplayValue()}
              </span>
            )}
            <div className="flex-shrink-0 cursor-pointer" onClick={handleIconClick}>
              {value.length > 0 ? (
                <X size={16} color="white" />
              ) : open ? (
                <ChevronUp size={16} color="white" />
              ) : (
                <ChevronDown size={16} color="white" />
              )}
            </div>
          </div>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            ref={contentRef}
            className="bg-gray-800 text-white rounded-md overflow-y-auto z-50 py-2 px-[10px] outline-none max-h-[420px]"
            align="start"
            sideOffset={14}
            alignOffset={-10}
            onOpenAutoFocus={event => {
              event.preventDefault();
              inputRef.current?.focus();
            }}
            style={{ width: dropdownWidth }}
          >
            <div className="sticky top-0 bg-gray-800 pt-1 pb-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={event => handleInputChange(event.target.value)}
                onBlur={handleInputBlur}
                className="w-full bg-gray-900 text-white text-[12px] outline-none px-2 py-2 rounded-md"
                placeholder="Поиск / вставка (Ctrl+V)"
                onClick={event => event.stopPropagation()}
              />
            </div>
            {filteredOptionsList.map(option => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <div
                  key={option.value}
                  className="flex items-center gap-[10px] cursor-pointer px-3 py-2 rounded hover:bg-[#2a2d32] outline-none"
                  onClick={event => handleOptionClick(event, option.value)}
                >
                  <div
                    className={`w-[14px] h-[14px] rounded-sm border-2 border-solid border-gray-600 flex items-center justify-center ${isSelected ? 'bg-blue-500 border-blue-500' : ''}`}
                  >
                    {isSelected && <Check size={12} color="white" />}
                  </div>
                  <div>{option.label}</div>
                </div>
              );
            })}
          </Popover.Content>
        </Popover.Portal>
      </div>
    </Popover.Root>
  );
};

export default CheckboxSelect;
